import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Col, Container, Row } from "reactstrap";

import porcherMark from "../../assets/images/porcher-logo.png";
import porcherWordmark from "../../assets/images/porcher-wordmark.png";
import "../Landing/landing.scss";

const freeFeatures = [
  { icon: "bx bx-user-circle", title: "Landlord account", description: "Create and use your Porcher account for free." },
  { icon: "bx bx-building-house", title: "Property listings", description: "Publish and manage rental listings at no cost." },
  { icon: "bx bx-file", title: "Rental applications", description: "Receive and review applications for free." },
];

const paymentFees = [
  {
    icon: "bx bx-transfer-alt",
    method: "ACH / bank payment",
    fee: "0.75%",
    description: "Paid by the tenant with each bank rent payment.",
  },
  {
    icon: "bx bx-credit-card",
    method: "Credit / debit card",
    fee: "3.75%",
    description: "Paid by the tenant with each card rent payment.",
  },
];

const PricingPage = () => {
  useEffect(() => {
    document.title = "Pricing | Porcher";
  }, []);

  return (
    <div className="porcher-page porcher-pricing-page">
      <nav className="porcher-navbar py-3">
        <Container className="d-flex align-items-center justify-content-between">
          <Link className="porcher-brand" to="/">
            <img src={porcherMark} alt="" className="p-mark" />
            <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
          </Link>
          <div className="d-flex align-items-center gap-2">
            <Link to="/" className="porcher-nav-link">Home</Link>
            <Link to="/login" className="porcher-nav-link">Sign In</Link>
            <Link to="/register" className="p-btn p-btn-primary d-none d-md-inline-flex">
              List Your Property
            </Link>
          </div>
        </Container>
      </nav>

      <main>
        <section className="porcher-pricing-hero">
          <Container>
            <span className="porcher-eyebrow">Simple, Transparent Pricing</span>
            <h1>Free for landlords.<br />Clear fees for tenants.</h1>
            <p>
              No subscription, no setup fee, and no cut of your rent. Landlords receive
              100% of the stated rent while tenants pay only for the services they use.
            </p>
            <div className="d-flex flex-wrap gap-3">
              <Link to="/register" className="p-btn p-btn-primary p-btn-lg">Get Started Free</Link>
              <Link to="/contact" className="p-btn p-btn-outline p-btn-lg">Contact Us</Link>
            </div>
          </Container>
        </section>

        <section className="porcher-section porcher-pricing-free">
          <Container>
            <div className="porcher-pricing-heading">
              <div>
                <span className="porcher-eyebrow">For Landlords</span>
                <h2 className="porcher-section-title">Everything you need to get started is free</h2>
              </div>
              <div className="porcher-free-price" aria-label="Free forever">
                <strong>$0</strong>
                <span>No monthly fee</span>
              </div>
            </div>
            <Row className="g-4 mt-2">
              {freeFeatures.map((feature) => (
                <Col md={4} key={feature.title}>
                  <div className="porcher-price-card porcher-price-card-free">
                    <i className={feature.icon} aria-hidden="true" />
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <span className="porcher-price-label">Free</span>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <section className="porcher-section porcher-section-soft">
          <Container>
            <span className="porcher-eyebrow">Pay Only When Used</span>
            <h2 className="porcher-section-title">Applicant and tenant-paid fees</h2>
            <p className="porcher-section-lead">
              Fees are shown before payment. There is no cap on rent-payment service fees.
            </p>

            <Row className="g-4 mt-3">
              <Col lg={4}>
                <div className="porcher-price-card porcher-screening-card">
                  <div className="porcher-price-card-top">
                    <i className="bx bx-search-alt-2" aria-hidden="true" />
                    <span className="porcher-paid-by">Paid by applicant</span>
                  </div>
                  <h3>Tenant screening</h3>
                  <div className="porcher-fee-amount">$50</div>
                  <p>One screening fee paid by the applicant.</p>
                </div>
              </Col>
              <Col lg={8}>
                <div className="porcher-rent-fees">
                  <div className="porcher-rent-fees-head">
                    <div>
                      <span className="porcher-paid-by">Paid by tenant</span>
                      <h3>Online rent payments</h3>
                    </div>
                    <span className="porcher-no-cap">No fee cap</span>
                  </div>
                  {paymentFees.map((payment) => (
                    <div className="porcher-payment-row" key={payment.method}>
                      <i className={payment.icon} aria-hidden="true" />
                      <div>
                        <h4>{payment.method}</h4>
                        <p>{payment.description}</p>
                      </div>
                      <strong>{payment.fee}</strong>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="porcher-section">
          <Container>
            <div className="porcher-landlord-payout">
              <div className="porcher-payout-icon"><i className="bx bx-wallet" aria-hidden="true" /></div>
              <div>
                <span className="porcher-eyebrow">Your Rent Stays Yours</span>
                <h2>Landlords receive 100% of the stated rent</h2>
                <p>Tenant service fees are added separately. Porcher does not deduct them from your rent payout.</p>
              </div>
              <div className="porcher-payout-stat"><strong>100%</strong><span>to landlord</span></div>
            </div>

            <div className="porcher-pricing-assurances">
              <span><i className="bx bx-check-circle" /> No monthly landlord subscription</span>
              <span><i className="bx bx-check-circle" /> No setup fee</span>
              <span><i className="bx bx-check-circle" /> Free listings and applications</span>
            </div>

            <div className="porcher-cta-band">
              <h2>Start managing your rental for free</h2>
              <p>Create your landlord account and list your first property with no upfront cost.</p>
              <Link to="/register" className="p-btn p-btn-primary p-btn-lg">List Your Property</Link>
            </div>
          </Container>
        </section>
      </main>

      <footer className="porcher-footer">
        <Container>
          <Row>
            <Col lg={5} className="mb-4 mb-lg-0">
              <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
              <p>Everything a self-managed homeowner needs to rent and manage a property.</p>
              <a href="mailto:contact@porcher.ai">contact@porcher.ai</a>
            </Col>
            <Col lg={3} md={6} className="mb-4 mb-lg-0">
              <h6>Product</h6>
              <ul>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/register">List Your Property</Link></li>
              </ul>
            </Col>
            <Col lg={4} md={6}>
              <h6>Legal</h6>
              <ul>
                <li><Link to="/terms-of-service">Terms of Service</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/payments-refund-policy">Payments &amp; Refunds</Link></li>
              </ul>
            </Col>
          </Row>
          <div className="porcher-footer-bottom">
            &copy; {new Date().getFullYear()} Porcher. All rights reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default PricingPage;